import React, { useMemo } from 'react';
import { Paper, Table, TableRow, TableCell, TableHead, Icon, TableBody, makeStyles, MenuItem, Button, IconButton, Menu, Hidden, Theme }
    from '@material-ui/core';
import clsx from 'clsx';
import { FileRequest, FileRequestTarget, FileRequestStatus, FileRequestAction } from '@moderepo/mode-apis';
import { BaseListCompFieldsSet, BaseListCompField, BaseListCompFieldsSettings, createHeaderColumn, BaseListCompProps, SelectInputOption,
    useModeTableStyle, useModePanelStyle, useDropdown, BaseListCompDataItem, createRemoveItemCell,
    createPreviewItemCell, CompPanelHeader } from '@moderepo/mode-app-components';


/**
 * The set of action the user can do to a request. NOTE: this is NOT the same as the FileRequestAction, download_from_robot, download_from_device, etc
 * This is the action the user can do to a request based on the current state of the request. For example, if a request is in PENDING state, maybe
 * the user can CANCEL the request. If a DOWNLOAD request is in SUCCESS state, maybe the user can DOWNLOAD the file, etc... There will be at most
 * 1 action the user can do for a request at a time. It is possible for a request to have no action too.
 */
export enum FileRequestUserAction {
    RELOAD_STATUS = 'reload',
    DOWNLOAD = 'download',
    CANCEL = 'cancel',
    DELETE = 'delete'
}


const useStyle = makeStyles((theme: Theme) => {
    return {

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        panelHeader: {
            flexDirection: 'row',
            flexWrap     : 'wrap',
        },

        panelHeaderIcon: {
        },

        filter: {
            '&:not(:last-child)': {
                marginRight: theme.spacing(2),
            },

            '& .label': {
                display          : 'flex',
                textTransform    : 'none',
                flexDirection    : 'column',
                alignItems       : 'flex-start',
                '& .filter-label': {
                    fontWeight: 'normal',
                    fontSize  : 'smaller',
                    color     : theme.palette.text.secondary,
                    lineHeight: '1em',
                },

                '& .filter-value': {
                    color: theme.palette.text.primary,
                },
            },
        },


        tableContainer: {
            overflow: 'auto',
            flex    : 1,
        },

        tableCol: {
            '&.status-col': {
                textAlign : 'center',
                '& .value': {
                    fontSize     : 'smaller',
                    fontWeight   : 500,
                    padding      : theme.spacing(0.3, 1),
                    borderRadius : 100,
                    textTransform: 'lowercase',
                    background   : theme.palette.info.main,
                    color        : theme.palette.info.contrastText,
                    width        : 'auto',
                    display      : 'inline-block',
                },

                [`&.status-${FileRequestStatus.CANCELED}`]: {
                    '& .value': {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.CANCELLING}`]: {
                    '& .value': {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.FAILED}`]: {
                    '& .value': {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.INITIATED}`]: {
                    '& .value': {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.PENDING}`]: {
                    '& .value': {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.RECEIVING}`]: {
                    '& .value': {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.SUCCEEDED}`]: {
                    '& .value': {
                        background: theme.palette.success.main,
                        color     : theme.palette.success.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.TIME_OUT}`]: {
                    '& .value': {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.WARNING}`]: {
                    '& .value': {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                },
                [`&.status-${FileRequestStatus.NOT_FOUND}`]: {
                    '& .value': {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
            },
            '&.id-col': {
                textAlign: 'center',
            },
            '&.deviceId-col': {
                textAlign: 'center',
            },
            '&.homeId-col': {
                textAlign: 'center',
            },
            '&.robotId-col': {
                textAlign: 'left',
            },
            '&.robotClass-col': {
                textAlign: 'left',
            },
            '&.target-col': {
                textAlign: 'left',
            },
            '&.requestAction-col': {
                textAlign: 'left',
                width    : '100%',
            },
            '&.errorMessage-col': {
                textAlign: 'left',
                width    : '100%',
            },
            '&.createdAt-col': {
                textAlign: 'left',
            },
            '&.updatedAt-col': {
                textAlign: 'left',
            },
            '&.expirationDate-col': {
                textAlign: 'left',
            },
            '&.action-col': {
                textAlign         : 'center',
                '& .action-button': {
                    margin: 0,
                },
                [`&.${FileRequestUserAction.RELOAD_STATUS}`]: {
                    '& .action-button:focus': {
                        animation: `$animate-checking-status 1000ms 1 ${theme.transitions.easing.easeInOut}`,
                    },
                },
            },
        },

        '@keyframes animate-checking-status': {
            from: {
                transform: 'rotateZ(0deg)',
            },

            to: {
                transform: 'rotateZ(-360deg)',
            },
        },
    };
}, { name: 'FileRequestsTable' });



/**
 * This is the data structure for the row data to be displayed in the table. The value for all these fields will be strings because
 * they will be displayed in the table. These values MUST be localized/formatted/processed by the container. The table is a dumb
 * component and does not know what these values mean so it will only display these values and nothing else.
 * For each column this table has, there should be a field in this data structure corresponding to that column.
 */
export interface RCDisplayableFileRequestsTableDataItem {
    readonly id?: string;
    readonly homeId?: string;
    readonly deviceId?: string;
    readonly robotId?: string;
    readonly robotClass?: string;
    readonly target?: string;
    readonly requestAction?: string;
    readonly status?: string;
    readonly errorMessage?: string;
    readonly createdAt?: string;
    readonly updatedAt?: string;
    readonly expirationDate?: string;
}


/**
 * Each row of table data will have 2 data, the display data (all strings) used for displaying in the table cell. The actual data used for adding
 * special UI for the data. Actual data is not used often but we will need it in some cases. For example, sometime the data contain some status
 * such as connected/disconnected, verified/not verified, etc... For these cases, we don't just want to show plain text but we also want to add
 * some color to highlight these statuses. For example, we want to show green background for connected or verified status and red background for
 * disconnected or not verified statuses. Therefore, this is the reason we need both displayed data and actual data for all table data.
 */
export interface RCFileRequestsTableDataItem extends BaseListCompDataItem<FileRequest> {
    readonly displayValue: RCDisplayableFileRequestsTableDataItem;

    // The action that the user can do for the request e.g. Cancel, Reload, Download, etc...
    readonly action?: FileRequestUserAction | undefined;
}


/**
 * Table columns setting for FileRequests table. FileRequests table columns settings MUST contain these column keys
 */
interface RCFileRequestsTableFieldsSet extends BaseListCompFieldsSet {
    readonly id?: BaseListCompField;
    readonly homeId?: BaseListCompField;
    readonly deviceId?: BaseListCompField;
    readonly robotId?: BaseListCompField;
    readonly robotClass?: BaseListCompField;
    readonly target?: BaseListCompField;
    readonly requestAction?: BaseListCompField;
    readonly status?: BaseListCompField;
    readonly errorMessage?: BaseListCompField;
    readonly createdAt?: BaseListCompField;
    readonly updatedAt?: BaseListCompField;
    readonly expirationDate?: BaseListCompField;
    readonly action?: BaseListCompField;
}



export interface RCFileRequestsTableFilters {
    readonly target?: {
        readonly currentValue: FileRequestTarget | undefined;
        readonly label: string;
        readonly options: readonly SelectInputOption<FileRequestTarget | ''>[];
        readonly onChange: (value: FileRequestTarget | '')=> void;
    }
    readonly action?: {
        readonly currentValue: FileRequestAction | undefined;
        readonly label: string;
        readonly options: readonly SelectInputOption<FileRequestAction | ''>[];
        readonly onChange: (value: FileRequestAction | '')=> void;
    }
    readonly status?: {
        readonly currentValue: FileRequestStatus | undefined;
        readonly label: string;
        readonly options: readonly SelectInputOption<FileRequestStatus | ''>[];
        readonly onChange: (value: FileRequestStatus | '')=> void;
    }
}


export interface RCFileRequestsTableProps extends BaseListCompProps<FileRequest> {
    readonly listData: readonly RCFileRequestsTableDataItem[] | undefined;
    readonly fieldsSettings: BaseListCompFieldsSettings<RCFileRequestsTableFieldsSet>;
    readonly filters?: RCFileRequestsTableFilters;

    // The tooltip text to use for the user actions. Only needed if the container provide user actions
    readonly texts?: {
        readonly reloadRequestStatusTooltip?: string | undefined;
        readonly downloadRequestTooltip?: string | undefined;
        readonly cancelRequestTooltip?: string | undefined;
        readonly deleteRequestTooltip?: string | undefined;
    };

    // Handlers for user actions and only needed if the table item has user action. And the container only need to implement the handler for
    // the actions that it needs. For example, if the container doesn't use DELETE action then it doesn't need to implement the onDeleteFileRequest.
    readonly onReloadFileRequestStatus?: (fileRequest: FileRequest)=> void;
    readonly onDownloadFileRequestFile?: (fileRequest: FileRequest)=> void;
    readonly onCancelFileRequest?: (fileRequest: FileRequest)=> void;
    readonly onDeleteFileRequest?: (fileRequest: FileRequest)=> void;
}



/**
 * This is a DUMB component that is in charge of displaying the list of FileRequests with pagination. All this component does is display data
 * provided through props. This component does not have any logic and any user action will be dispatched to the container's handlers.
 */
export const RCFileRequestsTable: React.FC<RCFileRequestsTableProps> = (props: RCFileRequestsTableProps) => {
    const [targetFilterMenuAnchorEl, isTargetFilterMenuOpened, onTargetFilterAnchorElClicked, onTargetFilterMenuClosed] = useDropdown();
    const [actionFilterMenuAnchorEl, isActionFilterMenuOpened, onActionFilterAnchorElClicked, onActionFilterMenuClosed] = useDropdown();
    const [statusFilterMenuAnchorEl, isStatusFilterMenuOpened, onStatusFilterAnchorElClicked, onStatusFilterMenuClosed] = useDropdown();

    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();
    const classes = useStyle();



    /**
     * Get the label of the currently selected option so we can display the selected value in the dropdown
     */
    const currentTargetLabel = useMemo(() => {
        if (props.filters?.target) {
            const selectedOption = props.filters.target.options.find((option: SelectInputOption<FileRequestTarget | ''>) => {
                return option.value === (props.filters?.target?.currentValue || '');
            });
            return selectedOption?.label || '';
        }
        return '';
    }, [props.filters]);


    /**
     * Get the label of the currently selected option so we can display the selected value in the dropdown
     */
    const currentActionLabel = useMemo(() => {
        if (props.filters?.action) {
            const selectedOption = props.filters.action.options.find((option: SelectInputOption<FileRequestAction | ''>) => {
                return option.value === (props.filters?.action?.currentValue || '');
            });
            return selectedOption?.label || '';
        }
        return '';
    }, [props.filters]);


    /**
     * Get the label of the currently selected option so we can display the selected value in the dropdown
     */
    const currentStatusLabel = useMemo(() => {
        if (props.filters?.status) {
            const selectedOption = props.filters.status.options.find((option: SelectInputOption<FileRequestStatus | ''>) => {
                return option.value === (props.filters?.status?.currentValue || '');
            });
            return selectedOption?.label || '';
        }
        return '';
    }, [props.filters]);


    return (
        <Paper elevation={2} className={clsx(panelClasses.root, props.className)}>
            <CompPanelHeader
                {...props}
                customContent={(
                    <>
                        {props.filters?.target && (
                            <>
                                <Hidden xsDown>
                                    <Button
                                        className={classes.filter}
                                        onClick={onTargetFilterAnchorElClicked}
                                        endIcon={<Icon>arrow_drop_down</Icon>}
                                    >
                                        <div className="label">
                                            <div className="filter-label">
                                                {props.filters.target.label}
                                            </div>
                                            <div className="filter-value">
                                                {currentTargetLabel}
                                            </div>
                                        </div>
                                    </Button>
                                </Hidden>
                                <Hidden smUp>
                                    <IconButton
                                        className={classes.filter}
                                        onClick={onTargetFilterAnchorElClicked}
                                    >
                                        <Icon>filter_list</Icon>
                                    </IconButton>
                                </Hidden>

                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={targetFilterMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    open={isTargetFilterMenuOpened}
                                    onClose={onTargetFilterMenuClosed}
                                >
                                    {props.filters.target.options.map((option: SelectInputOption<FileRequestTarget | ''>) => {
                                        return (
                                            <MenuItem
                                                key={option.label}
                                                selected={option.value === props.filters?.target?.currentValue}
                                                onClick={() => {
                                                    onTargetFilterMenuClosed();
                                                    if (props.filters?.target?.onChange) {
                                                        props.filters.target.onChange(option.value);
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </>
                        )}
                        {props.filters?.action && (
                            <>
                                <Hidden xsDown>
                                    <Button
                                        className={classes.filter}
                                        onClick={onActionFilterAnchorElClicked}
                                        endIcon={<Icon>arrow_drop_down</Icon>}
                                    >
                                        <div className="label">
                                            <div className="filter-label">
                                                {props.filters.action.label}
                                            </div>
                                            <div className="filter-value">
                                                {currentActionLabel}
                                            </div>
                                        </div>
                                    </Button>
                                </Hidden>
                                <Hidden smUp>
                                    <IconButton
                                        className={classes.filter}
                                        onClick={onActionFilterAnchorElClicked}
                                    >
                                        <Icon>filter_list</Icon>
                                    </IconButton>
                                </Hidden>

                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={actionFilterMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    open={isActionFilterMenuOpened}
                                    onClose={onActionFilterMenuClosed}
                                >
                                    {props.filters.action.options.map((option: SelectInputOption<FileRequestAction | ''>) => {
                                        return (
                                            <MenuItem
                                                key={option.label}
                                                selected={option.value === props.filters?.action?.currentValue}
                                                onClick={() => {
                                                    onActionFilterMenuClosed();
                                                    if (props.filters?.action?.onChange) {
                                                        props.filters.action.onChange(option.value);
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </>
                        )}
                        {props.filters?.status && (
                            <>
                                <Hidden xsDown>
                                    <Button
                                        className={classes.filter}
                                        onClick={onStatusFilterAnchorElClicked}
                                        endIcon={<Icon>arrow_drop_down</Icon>}
                                    >
                                        <div className="label">
                                            <div className="filter-label">
                                                {props.filters.status.label}
                                            </div>
                                            <div className="filter-value">
                                                {currentStatusLabel}
                                            </div>
                                        </div>
                                    </Button>
                                </Hidden>
                                <Hidden smUp>
                                    <IconButton
                                        className={classes.filter}
                                        onClick={onStatusFilterAnchorElClicked}
                                    >
                                        <Icon>filter_list</Icon>
                                    </IconButton>
                                </Hidden>

                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={statusFilterMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    open={isStatusFilterMenuOpened}
                                    onClose={onStatusFilterMenuClosed}
                                >
                                    {props.filters.status.options.map((option: SelectInputOption<FileRequestStatus | ''>) => {
                                        return (
                                            <MenuItem
                                                key={option.label}
                                                selected={option.value === props.filters?.status?.currentValue}
                                                onClick={() => {
                                                    onStatusFilterMenuClosed();
                                                    if (props.filters?.status?.onChange) {
                                                        props.filters.status.onChange(option.value);
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </>
                        )}
                    </>
                )}
            />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {props.listData && props.listData.length > 0 && (
                    <>
                        <div className={classes.tableContainer}>
                            <Table className={tableClasses.root} size="medium">
                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        {/* filter out hidden columns first and then go through each column to create Header Cell */}
                                        {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                            return !props.fieldsSettings.fields[fieldName]?.hidden;
                                        }).map((fieldName: string) => {
                                            const field = props.fieldsSettings.fields[fieldName];
                                            if (field && !field.hidden) {
                                                return createHeaderColumn(props.fieldsSettings, fieldName, field,
                                                    `${tableClasses.tableCol} ${classes.tableCol} ${fieldName}-col`);
                                            }
                                            return <></>;
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {props.listData.map((dataItem: RCFileRequestsTableDataItem): JSX.Element => {
                                        return (
                                            <TableRow
                                                className={clsx(
                                                    tableClasses.tableBodyRow, 'clickable', dataItem.selected && 'selected',
                                                )}
                                                key={dataItem.actualValue.id}
                                                onClick={() => {
                                                    if (props.onListItemSelected) {
                                                        props.onListItemSelected(dataItem.actualValue);
                                                    }
                                                }}
                                            >
                                                {/* filter out hidden columns first and then go through each column to create Table Cell */}
                                                {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                                    return !props.fieldsSettings.fields[fieldName]?.hidden;
                                                }).map((key: string) => {
                                                    const fieldName = key as keyof RCFileRequestsTableProps['fieldsSettings']['fields'];

                                                    /* Create table cell for SPECIAL fields which don't display the value as string */
                                                    if (fieldName === 'status' && props.fieldsSettings.fields.status
                                                        && !props.fieldsSettings.fields.status.hidden) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`,
                                                                    `status-${dataItem.actualValue.status}`)}
                                                            >
                                                                <div className="value">
                                                                    {dataItem.displayValue.status}
                                                                </div>
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (fieldName === 'action' && props.fieldsSettings.fields.action
                                                    && !props.fieldsSettings.fields.action.hidden) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(
                                                                    tableClasses.tableCol, classes.tableCol, `${fieldName}-col`, dataItem.action,
                                                                )}
                                                            >
                                                                {dataItem.action === FileRequestUserAction.RELOAD_STATUS && (
                                                                    <IconButton
                                                                        className="action-button"
                                                                        size="small"
                                                                        title={props.texts?.reloadRequestStatusTooltip}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            if (props.onReloadFileRequestStatus) {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                props.onReloadFileRequestStatus(dataItem.actualValue);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon color="primary">sync</Icon>
                                                                    </IconButton>
                                                                )}
                                                                {dataItem.action === FileRequestUserAction.DOWNLOAD && (
                                                                    <IconButton
                                                                        className="action-button"
                                                                        size="small"
                                                                        title={props.texts?.downloadRequestTooltip}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            if (props.onDownloadFileRequestFile) {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                props.onDownloadFileRequestFile(dataItem.actualValue);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon color="primary">get_app</Icon>
                                                                    </IconButton>
                                                                )}
                                                                {dataItem.action === FileRequestUserAction.CANCEL && (
                                                                    <IconButton
                                                                        className="action-button"
                                                                        size="small"
                                                                        title={props.texts?.cancelRequestTooltip}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            if (props.onCancelFileRequest) {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                props.onCancelFileRequest(dataItem.actualValue);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon color="error">cancel</Icon>
                                                                    </IconButton>
                                                                )}
                                                                {dataItem.action === FileRequestUserAction.DELETE && (
                                                                    <IconButton
                                                                        className="action-button"
                                                                        size="small"
                                                                        title={props.texts?.deleteRequestTooltip}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            if (props.onDeleteFileRequest) {
                                                                                event.stopPropagation();
                                                                                event.preventDefault();
                                                                                props.onDeleteFileRequest(dataItem.actualValue);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon color="error">delete</Icon>
                                                                    </IconButton>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (fieldName === 'remove' && props.fieldsSettings.fields.remove
                                                        && !props.fieldsSettings.fields.remove.hidden) {
                                                        return (
                                                            // NOTE: dataItem.canBeRemoved must be true
                                                            createRemoveItemCell(dataItem, clsx(
                                                                tableClasses.tableCol, classes.tableCol, `${fieldName}-col`,
                                                            ),
                                                            props.onRemoveItemClicked)
                                                        );
                                                    }
                                                    if (fieldName === 'preview' && props.fieldsSettings.fields.preview
                                                        && !props.fieldsSettings.fields.preview.hidden) {
                                                        return createPreviewItemCell(
                                                            dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`),
                                                            props.onPreviewItemClicked,
                                                        );
                                                    }

                                                    /**
                                                     * Handle all the other fields that don't have special UI. These are the fields that
                                                     * display dataItem.displayValue as plain text
                                                     */
                                                    if (props.fieldsSettings.fields[fieldName]
                                                        && !(props.fieldsSettings.fields[fieldName] as BaseListCompField).hidden) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`)}
                                                            >
                                                                {dataItem.displayValue[fieldName]}
                                                            </TableCell>
                                                        );
                                                    }

                                                    return <></>;
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            {props.listData && props.listData.length === 0 && props.emptyDataMessageComp}
                        </div>

                        {props.paginationComp}
                    </>
                )}

                {!props.listData && props.noDataMessageComp}
            </div>
        </Paper>
    );
};
